import { isEmpty } from 'lodash-es'
import { useTranslations } from 'next-intl'

import { NoSearchResult, TRoomPreview } from '@/entities/room'
import { cn } from '@/shared/lib'

import { MobileRoomItem, MobileRoomItemSkeleton } from '../room-item'

type Props = {
  isLoading: boolean
  rooms: TRoomPreview[]
  sentinelRef: (node?: Element | null) => void
}

const GRID_COLUMNS = 2

export const MobileRoomList = ({ isLoading, rooms, sentinelRef }: Props) => {
  if (isEmpty(rooms)) {
    return <NoResultMessage />
  }

  return (
    <ul className="md:grid md:grid-cols-2 md:gap-x-10">
      {(() => {
        if (isLoading) {
          return Array.from({ length: 4 }).map((_, index) => {
            const isFirstRow = index < GRID_COLUMNS
            const isLastRow = index >= GRID_COLUMNS

            return (
              <li
                key={index}
                className={cn(
                  'group border-b border-gray-100 last:border-none',
                  isLastRow && 'md:border-none',
                )}
              >
                <MobileRoomItemSkeleton
                  className={cn(
                    'py-5',
                    isFirstRow && 'md:pt-0',
                    isLastRow && 'md:pb-0',
                  )}
                />
              </li>
            )
          })
        }

        return rooms.map((room, index) => {
          const isLastItem = index === rooms.length - 1
          const isFirstRow = index < GRID_COLUMNS
          const isLastRow =
            index >=
            rooms.length - (rooms.length % GRID_COLUMNS || GRID_COLUMNS)

          return (
            <li
              key={room.rid}
              ref={isLastItem ? sentinelRef : null}
              className={cn(
                'group border-b border-gray-100 last:border-none',
                isLastRow && 'md:border-none',
              )}
            >
              <MobileRoomItem
                room={room}
                className={cn(
                  'group-first:pt-0 group-last:pb-0',
                  isFirstRow && 'md:pt-0',
                  isLastRow && 'md:pb-0',
                )}
              />
            </li>
          )
        })
      })()}
    </ul>
  )
}

const NoResultMessage = () => {
  const t = useTranslations('search_result')

  return (
    <div className="flex w-full shrink-0 flex-col items-center justify-center pt-25">
      <NoSearchResult className="mb-6" />
      <h3 className="typo-headline3 mb-4 text-center font-bold whitespace-break-spaces text-black">
        {t('empty_result.title')}
      </h3>
      <p className="typo-label1 text-center text-gray-900">
        {t('empty_result.description')}
      </p>
    </div>
  )
}
